import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { IMAGES } from "../lib/images";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(email, password);
      const to = loc.state?.from?.pathname || (u.role === "admin" ? "/admin" : "/portal");
      nav(to, { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Image side */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={IMAGES.tampa_skyline} alt="Tampa skyline" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-background/60 via-background/20 to-background/70" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="font-serif text-2xl tracking-tight">ELLIOTT</Link>
          <div>
            <div className="overline mb-4">— Investor Portal</div>
            <h2 className="font-serif text-5xl leading-[1.05] font-light">
              Confidential access<br />for capital partners.
            </h2>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center px-6 py-16 bg-background">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="w-full max-w-md">
          <Link to="/" className="lg:hidden font-serif text-2xl tracking-tight">ELLIOTT</Link>
          <div className="overline mt-6 mb-3">— Sign in</div>
          <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight">Investor Portal</h1>
          <p className="mt-3 text-sm text-muted-foreground">Confidential access for qualified investors and EID administrators.</p>

          <form onSubmit={submit} data-testid="login-form" className="mt-10 space-y-5">
            <div>
              <label className="overline mb-2 block">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-card border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors" placeholder="you@firm.com" />
            </div>
            <div>
              <label className="overline mb-2 block">Password</label>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-card border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors" placeholder="••••••••" />
            </div>
            {err && <div data-testid="login-error" className="text-sm text-destructive">{err}</div>}
            <button data-testid="login-submit" type="submit" disabled={busy} className="group w-full inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-5 text-[13px] uppercase tracking-[0.22em] hover:bg-primary/90 transition-all disabled:opacity-60">
              {busy ? "Signing in…" : "Sign In"} <ArrowUpRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-8 flex items-center justify-between text-sm">
            <Link to="/register" data-testid="login-to-register" className="text-primary hover:underline">Request access</Link>
            <Link to="/" className="text-foreground/60 hover:text-primary">← Home</Link>
          </div>

          <div className="mt-10 glass p-5">
            <div className="overline mb-2 text-primary/80">— Demo investor access</div>
            <div className="font-mono text-xs leading-relaxed text-foreground/70">
              <div>investor@elliottdev.com / Investor2026!</div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
